import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { PointLight as BABYLON_PointLight } from '@inter-ikea-betrakta-babylonjs/core/Lights/pointLight.js';
import { IVector3 } from '../api.js';
// TODO: Remove the "Babylon" prefix of class name after light system overhaul (right now there's already a class called LineLight)
export class BabylonLineLight extends BABYLON_PointLight {
    constructor(name, line_a, line_b, line_normal, angle_range_radians, scene, clip_plane) {
        const position = BABYLON_Vector3.Lerp(line_a, line_b, 0.5);
        super(name, position, scene);
        this.line_a = line_a;
        this.line_b = line_b;
        this.line_normal = line_normal;
        this.angle_range_radians = angle_range_radians;
        this.hard_side_cutoff = false;
        this._transformed_line_a = BABYLON_Vector3.Zero();
        this._transformed_line_b = BABYLON_Vector3.Zero();
        this._transformed_line_normal = BABYLON_Vector3.Zero();
        this.transformedPosition = BABYLON_Vector3.Zero();
        this.clip_plane = clip_plane;
        // this.range = 0.0
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _buildUniformLayout() {
        /**
        * Adds an uniform in the buffer.
        * Warning : the subsequents calls of this function must be in the same order as declared in the shader
        * for the layout to be correct !
        */
        this._uniformBuffer.addUniform('vLightData', 4);
        this._uniformBuffer.addUniform('vLightDiffuse', 4);
        this._uniformBuffer.addUniform('vLightSpecular', 4);
        this._uniformBuffer.addUniform('vLightFalloff', 4);
        this._uniformBuffer.addUniform(BabylonLineLight.LINE_A_UNIFORM, 3);
        this._uniformBuffer.addUniform(BabylonLineLight.LINE_B_UNIFORM, 3);
        this._uniformBuffer.addUniform(BabylonLineLight.LINE_NORMAL_AND_ANGLE_RANGE_UNIFORM, 4);
        this._uniformBuffer.addUniform(BabylonLineLight.LINE_CLIP_PLANE_NORMAL_DISTANCE, 4);
        this._uniformBuffer.addUniform('depthValues', 2);
        this._uniformBuffer.create();
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    computeTransformedInformation() {
        if (this.parent && this.parent.getWorldMatrix) {
            super.computeTransformedInformation();
            BABYLON_Vector3.TransformCoordinatesToRef(this.line_a, this.parent.getWorldMatrix(), this._transformed_line_a);
            BABYLON_Vector3.TransformCoordinatesToRef(this.line_b, this.parent.getWorldMatrix(), this._transformed_line_b);
            BABYLON_Vector3.TransformCoordinatesToRef(this.line_normal, this.parent.getWorldMatrix(), this._transformed_line_normal);
            return true;
        }
        else {
            this._transformed_line_a.copyFrom(this.line_a);
            this._transformed_line_b.copyFrom(this.line_b);
            this._transformed_line_normal.copyFrom(this.line_normal);
        }
        return false;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    transferToEffect(effect, lightIndex) {
        this.computeTransformedInformation();
        // https://github.com/BabylonJS/Babylon.js/blob/5e6321d887637877d8b28b417410abbbeb651c6e/src/Shaders/ShadersInclude/pbrDirectLightingFalloffFunctions.fx
        // range and inverse squared range will not do anything because we use physical light falloff
        this._uniformBuffer.updateFloat4('vLightFalloff', this.range, this._inverseSquaredRange, this.hard_side_cutoff ? 1.0 : 0.0, 0.0, lightIndex);
        this._uniformBuffer.updateFloat4('vLightData', this.transformedPosition.x, this.transformedPosition.y, this.transformedPosition.z, 0.0, lightIndex);
        this._uniformBuffer.updateFloat3(BabylonLineLight.LINE_A_UNIFORM, this._transformed_line_a.x, this._transformed_line_a.y, this._transformed_line_a.z, lightIndex);
        this._uniformBuffer.updateFloat3(BabylonLineLight.LINE_B_UNIFORM, this._transformed_line_b.x, this._transformed_line_b.y, this._transformed_line_b.z, lightIndex);
        this._uniformBuffer.updateFloat4(BabylonLineLight.LINE_NORMAL_AND_ANGLE_RANGE_UNIFORM, this._transformed_line_normal.x, this._transformed_line_normal.y, this._transformed_line_normal.z, Math.cos(this.angle_range_radians * 0.5), lightIndex);
        if (this.clip_plane !== null) {
            this._uniformBuffer.updateFloat4(BabylonLineLight.LINE_CLIP_PLANE_NORMAL_DISTANCE, this.clip_plane.normal.x, this.clip_plane.normal.y, this.clip_plane.normal.z, IVector3.dot(this.clip_plane.normal, this.clip_plane.position), lightIndex);
        }
        else {
            // Set a zero-vector, this will cause the clipping test to always succeed in the shader.
            this._uniformBuffer.updateFloat4(BabylonLineLight.LINE_CLIP_PLANE_NORMAL_DISTANCE, 0, 0, 0, 0, lightIndex);
        }
        this._uniformBuffer.updateFloat2('depthValues', 0.0, 0.0, lightIndex);
        return this;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prepareLightSpecificDefines(defines, lightIndex) {
        defines['LINELIGHT' + lightIndex] = true;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    get shadowEnabled() {
        return false;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-empty-function
    set shadowEnabled(value) {
        console.error('[IPEX Viewer] Can\'t set shadowEnable. Shadows is not supported for Line lights');
    }
}
BabylonLineLight.LINE_A_UNIFORM = 'vLineA';
BabylonLineLight.LINE_B_UNIFORM = 'vLineB';
BabylonLineLight.LINE_NORMAL_AND_ANGLE_RANGE_UNIFORM = 'vLineNormalAndAngleRange';
BabylonLineLight.LINE_CLIP_PLANE_NORMAL_DISTANCE = 'vLineClipPlaneNormalDistance';
//# sourceMappingURL=BabylonLineLight.js.map